/// <reference lib="dom" />

type AlphabetKey = Lowercase<string> | Uppercase<string>;
type DigitKey = `${0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`;
type ArrowKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';
type FunctionKey = `F${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`;
type SpecialKey =
  | 'Enter' | 'Escape' | 'Backspace' | 'Tab' | 'Space'
  | 'Shift' | 'Control' | 'Alt' | 'Meta'
  | 'CapsLock' | 'Delete' | 'Insert' | 'Home' | 'End' | 'PageUp' | 'PageDown';

export type ValidKey = AlphabetKey | DigitKey | ArrowKey | FunctionKey | SpecialKey;

export interface Coordinate {
  x: number;
  y: number;
}

export interface ClickDownOptions extends Coordinate {
  timeDown?: number;
}

export interface MouseMoveOptions {
  from?: Coordinate;
  to: Coordinate;
  timeToMove: number;
}

/** Options for one key event `InputSimulator` dispatches. */
export interface KeyOptions {
  /**
   * The legacy `keyCode` the event reports — as `which` too — in place of the code the simulator derives from the
   * key. A whole number ≥ 0, or the call throws. A release given none reports the code its press reported.
   */
  keyCode?: number;
}

/** `keyDown`'s options: a `keyCode`, and a time after which the key releases itself. */
export interface KeyDownOptions extends KeyOptions {
  /** Release the key after this many ms (`0`: never); the release reports the press's `keyCode`. */
  timePressedDown?: number;
}

const keyCodeMap: Record<string, number> = {
  // Arrow keys
  ArrowLeft: 37,
  ArrowUp: 38,
  ArrowRight: 39,
  ArrowDown: 40,

  // Control keys
  Enter: 13,
  Escape: 27,
  Backspace: 8,
  Tab: 9,
  Space: 32,

  // Modifier keys
  Shift: 16,
  Control: 17,
  Alt: 18,
  Meta: 91,

  // Navigation
  CapsLock: 20,
  Delete: 46,
  Insert: 45,
  Home: 36,
  End: 35,
  PageUp: 33,
  PageDown: 34,

  // Function keys
  F1: 112, F2: 113, F3: 114, F4: 115,
  F5: 116, F6: 117, F7: 118, F8: 119,
  F9: 120, F10: 121, F11: 122, F12: 123,
};

/**
 * The legacy codes a US-layout keyboard reports for the printable characters that are neither letters nor digits:
 * Playwright's US layout table (`USKeyboardLayout` in playwright-core's lib/server/usKeyboardLayout.js — each key's
 * `keyCode`, and its `shiftKey` character's), which headless Chromium 139 reports for real presses of the same keys
 * too (checked: all 32 of these). A shifted character reports the code of the key that types it (`:` is `;`'s 186,
 * `!` is `1`'s 49). Their char codes are other keys' legacy codes — `.` 46 is Delete, `'` 39 ArrowRight, `(` 40
 * ArrowDown — so a legacy reader would act on the wrong key.
 */
const punctuationKeyCodes: Record<string, number> = {
  ';': 186, ':': 186,
  '=': 187, '+': 187,
  ',': 188, '<': 188,
  '-': 189, '_': 189,
  '.': 190, '>': 190,
  '/': 191, '?': 191,
  '`': 192, '~': 192,
  '[': 219, '{': 219,
  '\\': 220, '|': 220,
  ']': 221, '}': 221,
  "'": 222, '"': 222,
  ')': 48, '!': 49, '@': 50, '#': 51, '$': 52, '%': 53, '^': 54, '&': 55, '*': 56, '(': 57,
};

function resolveKeyCode(key: string): number {
  if (key.length === 1) {
    return punctuationKeyCodes[key] ?? key.toUpperCase().charCodeAt(0);
  }
  return keyCodeMap[key] ?? 0;
}

/** The `keyCode` `options` names, checked: a legacy key code is a whole number ≥ 0. */
function explicitKeyCode(options: KeyOptions | undefined, method: string): number | undefined {
  const keyCode = options?.keyCode;
  if (keyCode === undefined) return undefined;
  if (!Number.isInteger(keyCode) || keyCode < 0) {
    throw new Error(`InputSimulator.${method}: keyCode must be a whole number ≥ 0 — got ${String(keyCode)}.`);
  }
  return keyCode;
}

/**
 * Engines differ on the `KeyboardEvent` constructor's legacy `keyCode`/`which` init members (jsdom and Chromium keep
 * them); one that ignores them builds an event that reads 0 — which a legacy reader (`event.keyCode ?? event.which`,
 * as pixidom's TextField does) turns into a NUL character. Stamp them as own accessors when the event did not keep
 * them, so every key event this simulator dispatches reports its legacy code.
 */
function stampLegacyKeyCodes(event: KeyboardEvent, keyCode: number): KeyboardEvent {
  if (event.keyCode !== keyCode) Object.defineProperty(event, 'keyCode', { get: () => keyCode });
  if (event.which !== keyCode) Object.defineProperty(event, 'which', { get: () => keyCode });
  return event;
}

/** The presses and releases `InputSimulator.pointerGuard` records. */
const GUARDED_POINTER_EVENTS = ['pointerdown', 'pointerup', 'mousedown', 'mouseup'] as const;

/** What `InputSimulator.pointerGuard` returns: the synthesized presses and releases it recorded, by event type. */
export interface PointerGuard {
  /** The types recorded so far, in order — a snapshot that later events do not change. */
  seen(): readonly string[];
  /** Remove the guard's listeners and return everything it recorded; nothing is recorded after. */
  dispose(): readonly string[];
}

/**
 * Dispatch a synthetic pointer event so it propagates EXACTLY like a real
 * browser event.
 *
 * A real (trusted) click is HIT-TESTED: the browser finds the topmost element at
 * the cursor and makes IT the event target, so the event captures DOWN to that
 * element and bubbles back UP — firing listeners on the canvas (or whatever is
 * under the point) AND every ancestor. `document.dispatchEvent(...)` does the
 * opposite: it forces the target to `document` (the `clientX/clientY` payload is
 * inert — nothing reads it to choose a target), so the propagation path is only
 * `window → document` and a listener on a canvas CHILD never runs. That is why a
 * game that (correctly) does `canvas.addEventListener('click', …)` never saw a
 * simulated click.
 *
 * We replicate the real flow by dispatching on `elementFromPoint(x, y)` — the
 * exact hit-test the browser does. Falls back to `document` when the environment
 * can't hit-test (jsdom performs no layout, so `elementFromPoint` returns null;
 * node test stubs omit it entirely). Callers pass VIEWPORT coordinates — the
 * runner offsets a test's canvas-local x/y by the target client canvas's
 * bounding rect first, so the hit lands inside that canvas.
 */
function dispatchPointerEvent(type: string, x: number, y: number): void {
  const doc: Document | undefined =
    typeof document !== 'undefined' ? (document as Document) : undefined;
  const hit =
    doc && typeof doc.elementFromPoint === 'function' ? doc.elementFromPoint(x, y) : null;
  const target: EventTarget = hit ?? doc ?? (globalThis as unknown as EventTarget);
  target.dispatchEvent(
    new MouseEvent(type, { clientX: x, clientY: y, bubbles: true, cancelable: true }),
  );
}

/**
 * Synthetic input for tests.
 *
 * Keys are `keydown`/`keyup` `KeyboardEvent`s dispatched on `window` ONLY: a key listener on `window` hears them, one
 * on `document` or on an element never does (an event dispatched on `window` does not travel down the tree). There is
 * no `keypress` event — `simulateKeyPress` is a `keydown` and, 100 ms later, a `keyup` — so typing text into a DOM
 * widget that listens on `document` or reads `keypress` (pixidom's TextField does both) needs a driver of its own.
 * Every key event reports a legacy `keyCode`/`which` — the code a US-layout keyboard reports in Chromium, or the one
 * `KeyOptions.keyCode` names. Pointer events go to the element under the point and bubble from there.
 */
export class InputSimulator {
  /** Each key held down: the legacy `keyCode` its press reported (its release reports the same), and which press it is. */
  private readonly keyDownLookup: Map<string, { keyCode: number; press: number }>;
  /** Numbers the presses, so a release scheduled for one press can tell it is still the press that is down. */
  private presses = 0;

  constructor() {
    this.keyDownLookup = new Map();
  }

  /**
   * Dispatch a `keydown` on `window`; a key already held is not pressed again. `options` is the time after which the
   * key releases itself (a number, as `timePressedDown`), and/or the legacy `keyCode` the event reports. That release
   * is this press's own: once the key is up — released by hand, or pressed again since — it does nothing.
   */
  simulateKeyDown(key: ValidKey, options?: number | KeyDownOptions): void {
    const { timePressedDown, ...codeOptions }: KeyDownOptions =
      typeof options === 'number' ? { timePressedDown: options } : options ?? {};
    const keyCode = explicitKeyCode(codeOptions, 'simulateKeyDown') ?? resolveKeyCode(key);
    if (this.keyDownLookup.has(key)) return;

    const event = stampLegacyKeyCodes(new KeyboardEvent('keydown', {
      key,
      code: key.length === 1 ? `Key${key.toUpperCase()}` : key,
      keyCode,
      which: keyCode,
      bubbles: true,
      cancelable: true,
    }), keyCode);

    window.dispatchEvent(event);
    const press = ++this.presses;
    this.keyDownLookup.set(key, { keyCode, press });

    if (timePressedDown) {
      setTimeout(() => this.releasePress(key, press), timePressedDown);
    }
  }

  /** A scheduled release: `key`'s keyup, if `press` is still the press holding it down — otherwise nothing. */
  private releasePress(key: ValidKey, press: number): void {
    if (this.keyDownLookup.get(key)?.press !== press) return;
    this.simulateKeyUp(key);
  }

  /**
   * Dispatch a `keyup` on `window` for a key `simulateKeyDown` pressed (throws for one that is not down). It reports
   * the `keyCode` its press reported, unless `options` names another.
   */
  simulateKeyUp(key: ValidKey, options?: KeyOptions): void {
    const explicit = explicitKeyCode(options, 'simulateKeyUp');
    const held = this.keyDownLookup.get(key);
    if (held === undefined) {
      throw new Error(`simulateKeyUp: '${key}' was never pressed with simulateKeyDown.`);
    }

    const keyCode = explicit ?? held.keyCode;
    const event = stampLegacyKeyCodes(new KeyboardEvent('keyup', {
      key,
      code: key.length === 1 ? `Key${key.toUpperCase()}` : key,
      keyCode,
      which: keyCode,
      bubbles: true,
      cancelable: true,
    }), keyCode);

    window.dispatchEvent(event);
    this.keyDownLookup.delete(key);
  }

  /**
   * A `keydown` now and a `keyup` 100 ms later, both on `window` — there is no `keypress` event. `options.keyCode` is
   * reported by both. A key already held is not pressed again; the keyup then releases that hold. The release is the
   * held press's own: once the key is up — released by hand, or pressed again since — it does nothing.
   */
  simulateKeyPress(key: ValidKey, options?: KeyOptions): void {
    const keyCode = explicitKeyCode(options, 'simulateKeyPress');
    this.simulateKeyDown(key, { keyCode });
    // The key is down now — pressed here, or held already — and the keyup is that press's.
    const { press } = this.keyDownLookup.get(key) as { press: number };
    setTimeout(() => this.releasePress(key, press), 100);
  }

  simulateClick(x: number, y: number): void {
    // Hit-test the element at (x, y) and dispatch there so the click propagates
    // like a real one (capture down to the canvas/element, bubble back up) — not
    // only on `document`. See dispatchPointerEvent.
    dispatchPointerEvent('mousedown', x, y);
    dispatchPointerEvent('mouseup', x, y);
    dispatchPointerEvent('click', x, y);
  }

  async simulateClickDown({ x, y, timeDown }: ClickDownOptions): Promise<void> {
    dispatchPointerEvent('mousedown', x, y);

    if (timeDown) {
      return new Promise(resolve => {
        setTimeout(() => {
          this.simulateClickUp(x, y);
          resolve();
        }, timeDown);
      });
    }

    return Promise.resolve(); // Manual simulateClickUp expected
  }

  simulateClickUp(x: number, y: number): void {
    dispatchPointerEvent('mouseup', x, y);
    dispatchPointerEvent('click', x, y);
  }

  simulateMouseMove({ from, to, timeToMove }: MouseMoveOptions): Promise<void> {
    return new Promise(resolve => {
      const steps = 20;
      const interval = timeToMove / steps;
      const startX = from?.x ?? to.x;
      const startY = from?.y ?? to.y;
      const deltaX = (to.x - startX) / steps;
      const deltaY = (to.y - startY) / steps;

      let i = 0;
      const moveInterval = setInterval(() => {
        if (i > steps) {
          clearInterval(moveInterval);
          resolve();
          return;
        }

        dispatchPointerEvent(
          'mousemove',
          Math.round(startX + deltaX * i),
          Math.round(startY + deltaY * i),
        );
        i++;
      }, interval);
    });
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Static convenience facade (2026-05-19 snake e2e take10 regression)
  //
  // The sandbox-ai writeTests prompt (writeTestsNode.ts:189-198, 360-417) and
  // the diagnostic test fixtures across the codebase universally call
  // `InputSimulator.keyDown('d')`, `InputSimulator.keyUp('d')`, etc. as STATIC
  // methods on the class. The actual instance methods are named
  // `simulateKeyDown` / `simulateKeyUp` / etc. — no static aliases existed,
  // so every LLM-generated test that followed the prompt died with
  // `test_utils_1.InputSimulator.keyDown is not a function`.
  //
  // Rather than rewrite the prompt + every fixture (and re-train the LLM on
  // a new pattern), we add the documented static API here. Each static method
  // delegates to a process-level singleton instance — cross-test contamination
  // is possible via the singleton's `keyDownLookup` map; tests that need
  // isolation can call `InputSimulator.reset()` between cases.
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Process-level singleton used by the static facade methods below.
   * Exposed via `getSingleton()` for tests that want to assert on the
   * underlying state (e.g. `keyDownLookup`).
   */
  private static _singleton: InputSimulator | undefined;

  private static get singleton(): InputSimulator {
    if (!InputSimulator._singleton) {
      InputSimulator._singleton = new InputSimulator();
    }
    return InputSimulator._singleton;
  }

  /**
   * Reset the singleton's internal state. Call between independent test
   * scenarios that want a fresh key-tracking map. Tests that don't care
   * about cross-test leakage can skip this.
   */
  static reset(): void {
    InputSimulator._singleton = undefined;
  }

  // Documented static API — mirrors the writeTests prompt verbatim.

  /** `simulateKeyDown` on the shared instance: a `keydown` on `window` only. */
  static keyDown(key: ValidKey, options?: number | KeyDownOptions): void {
    InputSimulator.singleton.simulateKeyDown(key, options);
  }

  /** `simulateKeyUp` on the shared instance: a `keyup` on `window` only. */
  static keyUp(key: ValidKey, options?: KeyOptions): void {
    InputSimulator.singleton.simulateKeyUp(key, options);
  }

  /** `simulateKeyPress` on the shared instance: a `keydown` and, 100 ms later, a `keyup` — no `keypress`. */
  static keyPress(key: ValidKey, options?: KeyOptions): void {
    InputSimulator.singleton.simulateKeyPress(key, options);
  }

  static click(x: number, y: number): void {
    InputSimulator.singleton.simulateClick(x, y);
  }

  static clickDown(options: ClickDownOptions): Promise<void> {
    return InputSimulator.singleton.simulateClickDown(options);
  }

  static clickUp(x: number, y: number): void {
    InputSimulator.singleton.simulateClickUp(x, y);
  }

  /**
   * Mouse-move static facade. The prompt teaches the shape
   * `InputSimulator.mouseMove(x, y)` — a single-step move to a target
   * coordinate. The full programmatic `simulateMouseMove({ from, to,
   * timeToMove })` is still available on instances for tests that need
   * timed/multi-step moves.
   */
  static mouseMove(x: number, y: number): void {
    dispatchPointerEvent('mousemove', x, y);
  }

  /**
   * Record the synthesized presses and releases the page sees, so a keyboard-only drive can assert "zero pointer
   * events" as observed fact, not only by construction. Capture-phase listeners on `window` record the type of every
   * UNTRUSTED (`isTrusted === false`: script-dispatched) `pointerdown`, `pointerup`, `mousedown` and `mouseup`
   * dispatched on any element in this window's document — also one a listener below `window` stops, or one that does
   * not bubble. An event dispatched on an element outside the document, or in another document (an iframe's), never
   * reaches this `window` and is not recorded. A real user's pointer in a headed run is trusted and not recorded;
   * `click`, `mousemove` and wheel events are not recorded.
   */
  static pointerGuard(): PointerGuard {
    const seen: string[] = [];
    const onPointer = (event: Event) => {
      if (event.isTrusted === false) seen.push(event.type);
    };
    GUARDED_POINTER_EVENTS.forEach((type) => window.addEventListener(type, onPointer, true));
    return {
      seen: () => [...seen],
      dispose() {
        GUARDED_POINTER_EVENTS.forEach((type) => window.removeEventListener(type, onPointer, true));
        return [...seen];
      },
    };
  }

  /** `InputSimulator.pointerGuard()`, for a drive that holds its own instance. */
  pointerGuard(): PointerGuard {
    return InputSimulator.pointerGuard();
  }
}
